import { useMemo, useState } from 'react';
import { Archive, MoreHorizontal, Plus, CalendarClock } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type ColumnDef, type StatusTone } from '@vnetwork/ui/data';
import {
  Button,
  Badge,
  Skeleton,
  IconButton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { formatDate } from '@/lib/format-date';
import {
  useBackupList,
  useCreateBackup,
  useDeleteBackup,
  useScheduledActions,
  useDeleteScheduledAction,
  type Instance,
  type Backup,
  type ScheduledAction,
} from '@/api/opennebula';
import { ScheduledActionDialog, describeSchedule, formatScheduleTime } from './ScheduledActionDialog';
import { BackupRestoreDialog } from '../backups/BackupRestoreDialog';

const backupGB = (mb?: string | number) => {
  const n = Number(mb);
  return Number.isFinite(n) && n > 0 ? Math.round((n / 1024) * 10) / 10 : 0;
};

function statusTone(s?: unknown): StatusTone {
  const v = String(s ?? '').toLowerCase();
  if (v.includes('ready') || v.includes('done') || v.includes('active')) return 'success';
  if (v.includes('progress') || v.includes('pending') || v.includes('running')) return 'warning';
  if (v.includes('error') || v.includes('fail')) return 'danger';
  return 'muted';
}

export function BackupTab({ serviceId, instance }: { serviceId: string; instance: Instance }) {
  const { show } = useToast();
  const { data: allBackups = [], isLoading } = useBackupList(serviceId);
  const { data: schedules = [] } = useScheduledActions(serviceId, instance.id);
  const [createOpen, setCreateOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editSchedule, setEditSchedule] = useState<ScheduledAction | null>(null);
  const [restoreBackup, setRestoreBackup] = useState<Backup | null>(null);
  const [delBackup, setDelBackup] = useState<Backup | null>(null);
  const [delSchedule, setDelSchedule] = useState<ScheduledAction | null>(null);

  // A backup already running on this instance — disable Create until it settles
  // (mirrors @console, which gates the button on `state === 'BACKUP'`).
  const backupInProgress = instance.state === 'BACKUP' || instance.state === 'BACKUP_POWEROFF';

  // Backups are service-scoped; this tab shows only the ones taken from THIS
  // instance. An orphan backup (source VM deleted → no `attached`) has no home
  // instance, so it stays on the service-wide Backups page rather than leaking
  // onto every instance's tab.
  const backups = useMemo(
    () => allBackups.filter((b) => b.attached?.instance_id === instance.id),
    [allBackups, instance.id],
  );

  const createBackup = useCreateBackup(serviceId, instance.id, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Backup started' });
      setCreateOpen(false);
    },
    onError: () => show({ tone: 'danger', title: 'Backup failed' }),
  });
  const removeBackup = useDeleteBackup(serviceId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Backup deleted' });
      setDelBackup(null);
    },
    onError: () => show({ tone: 'danger', title: 'Delete failed' }),
  });
  const removeSchedule = useDeleteScheduledAction(serviceId, instance.id, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Schedule deleted' });
      setDelSchedule(null);
    },
    onError: () => show({ tone: 'danger', title: 'Delete failed' }),
  });

  const backupCols: ColumnDef<Backup, unknown>[] = [
    { id: 'name', header: 'Backup', cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name || row.original.id}</span> },
    {
      id: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <Badge tone={row.original.backup_increments == null ? 'info' : 'neutral'} appearance="subtle" size="sm">
          {row.original.backup_increments == null ? 'Full' : 'Incremental'}
        </Badge>
      ),
    },
    { id: 'persistent', header: 'Persistent', cell: ({ row }) => (row.original.persistent === '1' ? 'Yes' : 'No') },
    { id: 'size', header: 'Size', cell: ({ row }) => <span>{backupGB(row.original.size)} GB</span> },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusPill tone={statusTone(row.original.state)}>{row.original.state || 'Unknown'}</StatusPill>,
    },
    {
      id: 'created',
      header: 'Created',
      cell: ({ row }) => (
        <span style={{ color: 'var(--vn-fg-muted)' }}>
          {row.original.created_at ? formatDate(row.original.created_at as string) : '—'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton variant="ghost" size="sm" aria-label="Backup actions">
                <MoreHorizontal size={16} />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/ready/i.test(row.original.state ?? '') && (
                <DropdownMenuItem onSelect={() => setRestoreBackup(row.original)}>Restore</DropdownMenuItem>
              )}
              <DropdownMenuItem tone="danger" onSelect={() => setDelBackup(row.original)}>
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  const scheduleCols: ColumnDef<ScheduledAction, unknown>[] = [
    { id: 'action', header: 'Action', cell: ({ row }) => <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{(row.original.schedule_action || '—').replace(/-/g, ' ')}</span> },
    { id: 'time', header: 'Next run', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{formatScheduleTime(row.original.time)}</span> },
    { id: 'repeat', header: 'Repeat', cell: ({ row }) => <span>{describeSchedule(row.original).repeat}</span> },
    { id: 'ends', header: 'Ends', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{describeSchedule(row.original).ends}</span> },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton variant="ghost" size="sm" aria-label="Schedule actions">
                <MoreHorizontal size={16} />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setEditSchedule(row.original)}>Edit</DropdownMenuItem>
              <DropdownMenuItem tone="danger" onSelect={() => setDelSchedule(row.original)}>Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Section
        title="Backups"
        description="On-demand backups of this instance's disks."
        style={{ marginBottom: 0 }}
        actions={
          <Button
            size="sm"
            startIcon={<Plus size={15} />}
            disabled={createBackup.isPending || backupInProgress}
            onClick={() => setCreateOpen(true)}
          >
            Create backup
          </Button>
        }
      >
        {isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 44 }} />
            ))}
          </div>
        ) : (
          // Empty placeholder renders inside the table's own bordered frame
          // (via `emptyState`), so the zone keeps the same frame whether empty
          // or full — no layout jump between states.
          <DataTable
            columns={backupCols}
            data={backups}
            getRowId={(b) => String(b.id)}
            emptyState={
              <EmptyState icon={<Archive size={28} />} title="No backups" description="Create a backup to protect this instance's data." />
            }
          />
        )}
      </Section>

      <Section
        title="Scheduled actions"
        description="Recurring backups, snapshots and power actions."
        style={{ marginBottom: 0 }}
        actions={
          <Button size="sm" variant="secondary" startIcon={<CalendarClock size={15} />} onClick={() => setScheduleOpen(true)}>
            New schedule
          </Button>
        }
      >
        <DataTable
          columns={scheduleCols}
          data={schedules}
          getRowId={(s) => String(s.id)}
          emptyState={
            <EmptyState icon={<CalendarClock size={28} />} title="No scheduled actions" description="Automate recurring backups or power actions." />
          }
        />
      </Section>

      <ConfirmDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="Create backup?"
        description={`Start an on-demand backup of “${instance.name || instance.id}” now?`}
        confirmLabel="Create backup"
        loading={createBackup.isPending}
        onConfirm={() => createBackup.mutate(undefined)}
      />
      <BackupRestoreDialog backup={restoreBackup} onClose={() => setRestoreBackup(null)} />
      <ScheduledActionDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        serviceId={serviceId}
        instance={instance}
      />
      <ScheduledActionDialog
        open={!!editSchedule}
        onOpenChange={(o) => !o && setEditSchedule(null)}
        serviceId={serviceId}
        instance={instance}
        editing={editSchedule}
      />
      <ConfirmDialog
        open={!!delBackup}
        onOpenChange={(o) => !o && setDelBackup(null)}
        title="Delete backup?"
        description="This permanently removes the backup."
        confirmLabel="Delete"
        destructive
        loading={removeBackup.isPending}
        onConfirm={() => delBackup && removeBackup.mutate(delBackup.id)}
      />
      <ConfirmDialog
        open={!!delSchedule}
        onOpenChange={(o) => !o && setDelSchedule(null)}
        title="Delete scheduled action?"
        description="This removes the recurring action."
        confirmLabel="Delete"
        destructive
        loading={removeSchedule.isPending}
        onConfirm={() => delSchedule && removeSchedule.mutate(delSchedule.id)}
      />
    </div>
  );
}

